import * as Sentry from '@sentry/nextjs';
import { isJSON } from '@/components/shared/Hooks';
import { setSupervisionLock } from '@/store/slices/components/agentSupervision/AgentSupervisionSlice';
import { showUniqueToast } from '@/store/slices/components/toast/ToastSlice';

const storeAuth = {
  token: '',
  tokkoBrokerHost: '',
};

// Endpoints used during app bootstrap to load essential settings
const BOOTSTRAP_ENDPOINTS = [
  '/api3/users/config',
  '/api3/company/config',
  '/api3/users/permissions',
  '/api3/users/me',
];

// Transient backend failures (flaky Redis sessions/cache, DNS hiccups, gunicorn
// worker timeouts) intermittently surface as a network error, a 5xx, or a
// spurious 403/redirect for an otherwise-authenticated user. Retry with backoff
// before treating any of these as a lost session, so a healthy user is not
// bounced to /not_connected. A genuine logout still fails every retry.
const TRANSIENT_RETRY_ATTEMPTS = 2;
const TRANSIENT_RETRY_BASE_DELAY_MS = 600;
const IDEMPOTENT_METHODS = ['GET', 'HEAD', 'OPTIONS'];
const AGENT_SUPERVISION_LOCKED_ERROR_CODE = 'agent_supervision_locked';

// The store is loaded lazily: its slices import services that import this
// module, so a static import would be circular.
async function reportSupervisionLock(lock) {
  const { default: store } = await import('@/store/store');
  store.dispatch(setSupervisionLock(lock));
  store.dispatch(showUniqueToast({ text: lock.message, type: 'danger' }));
}
const wait = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

async function updateToken() {
  storeAuth.tokkoBrokerHost = localStorage.getItem('tokkoBrokerHost');
  storeAuth.token = localStorage.getItem('jwt');
}

class ApiService {
  static handleUnauthorizedRedirect(noRedirect) {
    if (process.env.NODE_ENV && !noRedirect) {
      if (typeof window !== 'undefined') {
        if (window.location.href.includes('outsidelayout')) {
          window.parent.postMessage(
            'outsidelayoutUnauthorized',
            storeAuth.tokkoBrokerHost
          );
        }
        window.location.assign(`${storeAuth.tokkoBrokerHost}/not_connected/`);
      }
    }
  }

  static stopAfterUnauthorizedRedirect(noRedirect) {
    this.handleUnauthorizedRedirect(noRedirect);
    console.warn('Redirecting');
    // prevents handling empty responses, without throw an error
    return new Promise(() => {});
  }

  static async getRequestOptions(
    method,
    requestData = null,
    isMultipart = false
  ) {
    if (storeAuth.token === '' && process.env.NODE_ENV !== 'test') {
      await updateToken();
    }
    const requestOptions = {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: storeAuth.token,
      },
      credentials: 'include',
    };

    if (requestData) {
      requestOptions.body = JSON.stringify(requestData);
    }

    if (isMultipart) {
      delete requestOptions.headers['Content-Type'];
      requestOptions.body = requestData;
    }

    return requestOptions;
  }

  static async performRequest(
    url,
    requestOptions,
    baseUrl,
    noRedirect = false,
    attempt = 0,
    skipTransientRetry = false
  ) {
    const composedURL = `${baseUrl || storeAuth.tokkoBrokerHost}${url}`;
    const pathWithoutQuery = typeof url === 'string' ? url.split('?')[0] : '';
    const isBootstrap = BOOTSTRAP_ENDPOINTS.includes(pathWithoutQuery);
    const method = (requestOptions?.method || 'GET').toUpperCase();
    const isIdempotent = IDEMPOTENT_METHODS.includes(method);
    const hasRetriesLeft = attempt < TRANSIENT_RETRY_ATTEMPTS;
    const retry = () =>
      wait(TRANSIENT_RETRY_BASE_DELAY_MS * (attempt + 1)).then(() =>
        this.performRequest(
          url,
          requestOptions,
          baseUrl,
          noRedirect,
          attempt + 1,
          skipTransientRetry
        )
      );

    let response;
    try {
      response = await fetch(composedURL, requestOptions);
    } catch (fetchError) {
      if (fetchError?.name === 'AbortError') {
        throw fetchError;
      }

      // Transient network/CORS blip on a bootstrap read: retry before assuming
      // the session is gone (these endpoints gate the whole app).
      if (isBootstrap && isIdempotent && hasRetriesLeft) {
        return retry();
      }

      if (process.env.NODE_ENV === 'production') {
        Sentry.withScope((scope) => {
          scope.setLevel('warning');
          scope.setTag('captured_by', 'ApiService');
          scope.setTag('kind', 'fetch_failed');
          scope.setFingerprint(['fetch_failed', composedURL]);
          scope.setContext('request', {
            url: composedURL,
            method: requestOptions?.method,
            noRedirect,
          });
          scope.setContext('network', {
            online:
              typeof navigator !== 'undefined'
                ? Boolean(navigator.onLine)
                : null,
            effectiveType:
              typeof navigator !== 'undefined' && navigator.connection
                ? navigator.connection.effectiveType
                : null,
          });
          Sentry.captureException(fetchError);
        });
      }

      // If fetch fails (network/CORS/redirect to non-CORS), redirect only for bootstrap endpoints
      if (isBootstrap) {
        this.handleUnauthorizedRedirect(noRedirect);
      }
      throw fetchError;
    }

    // Transient server error (e.g. a gunicorn worker timeout surfacing as 5xx)
    // on an idempotent request: retry instead of failing the call. Non-idempotent
    // methods are never auto-retried on 5xx to avoid duplicate writes.
    if (
      response.status >= 500 &&
      isIdempotent &&
      hasRetriesLeft &&
      !skipTransientRetry
    ) {
      return retry();
    }

    let data;
    if (process.env.NODE_ENV !== 'test') {
      const contentType = response.headers.get('Content-Type') || '';
      if (contentType.startsWith('application/vnd.ms-excel')) {
        data = await response.blob();
      } else if (response.headers.get('Content-Type') === 'application/json') {
        data = await response.json();
      } else {
        const text = await response.text();
        data = isJSON(text) ? JSON.parse(text) : {};
      }
    } else {
      data = await response.json();
    }

    const wasRedirected =
      response.redirected && response.url.includes('/not_connected');

    // DRF + SessionAuthentication answers an unauthenticated request with 403
    // (not 401), so 403/401/redirect-to-not_connected are the real "session
    // gone" signals on api3 - but a flaky Redis session backend produces the
    // same response transiently. Retry before bouncing so a backend blip does
    // not log out a healthy user; a real logout still fails every retry. Safe
    // for any method: the request was rejected before it executed.
    // An agent supervision lock is a 400 identified by its error_code: it
    // must reach the caller as a lock, not as a generic request error.
    const supervisionLocked =
      response.status === 400 &&
      data?.error_code === AGENT_SUPERVISION_LOCKED_ERROR_CODE;
    const authFailed =
      !noRedirect &&
      (wasRedirected || response.status === 401 || response.status === 403);

    if (authFailed && hasRetriesLeft) {
      return retry();
    }

    // fallback for redirect
    if (wasRedirected && !noRedirect) {
      return this.stopAfterUnauthorizedRedirect(noRedirect);
    }

    if (!response.ok) {
      // `status` is only ever set here, on a real backend HTTP response, so
      // callers use its presence to tell a server-authored `message` (safe
      // to show a user) apart from a native fetch failure (offline, CORS),
      // whose message is a browser-internal string instead.
      const errorMessage = data.message || data.error || 'Request failed';
      const error = new Error(errorMessage);
      error.status = response.status;
      error.response = data;

      if (supervisionLocked) {
        await reportSupervisionLock(data);
        error.supervisionLock = data;
        throw error;
      }

      // 302 is intentionally excluded: fetch follows redirects, so a genuine
      // login_required redirect is detected above via `wasRedirected`.
      const redirectStatuses = [401, 403];

      if (
        redirectStatuses.includes(response.status) &&
        process.env.NODE_ENV &&
        !noRedirect
      ) {
        this.handleUnauthorizedRedirect(noRedirect);
      }

      throw error;
    }

    return data;
  }

  static async parseJSON(response) {
    return response.text().then((text) => (text ? JSON.parse(text) : {}));
  }

  static async get(
    url,
    baseUrl = storeAuth.tokkoBrokerHost,
    noRedirect = false,
    skipTransientRetry = false
  ) {
    const requestOptions = await this.getRequestOptions('GET');
    return this.performRequest(
      url,
      requestOptions,
      baseUrl,
      noRedirect,
      0,
      skipTransientRetry
    );
  }

  static async post(
    url,
    requestData,
    baseUrl = storeAuth.tokkoBrokerHost,
    isMultipart = false,
    noRedirect = false
  ) {
    const requestOptions = await this.getRequestOptions(
      'POST',
      requestData,
      isMultipart
    );
    return this.performRequest(url, requestOptions, baseUrl, noRedirect);
  }

  static async delete(
    url,
    requestData,
    baseUrl = storeAuth.tokkoBrokerHost,
    noRedirect = false
  ) {
    const requestOptions = await this.getRequestOptions('DELETE', requestData);
    return this.performRequest(url, requestOptions, baseUrl, noRedirect);
  }

  static async put(
    url,
    requestData,
    baseUrl = storeAuth.tokkoBrokerHost,
    isMultipart = false,
    noRedirect = false
  ) {
    const requestOptions = await this.getRequestOptions(
      'PUT',
      requestData,
      isMultipart
    );
    return this.performRequest(url, requestOptions, baseUrl, noRedirect);
  }

  static async patch(
    url,
    requestData,
    baseUrl = storeAuth.tokkoBrokerHost,
    noRedirect = false,
    { signal, keepalive } = {}
  ) {
    const requestOptions = await this.getRequestOptions('PATCH', requestData);

    if (signal) {
      requestOptions.signal = signal;
    }

    if (keepalive) {
      requestOptions.keepalive = true;
    }

    return this.performRequest(url, requestOptions, baseUrl, noRedirect);
  }
}

export default ApiService;
